import { diff_match_patch } from "diff-match-patch"

const DIFF_DELETE = -1
const DIFF_INSERT = 1
const MAX_LINE_TOKENS = 65_535

type DiffOperation = -1 | 0 | 1

export interface ApplyDiffValue {
  kind: "json" | "text" | "unavailable"
  previous: string
  proposed: string
}

export interface ApplyDiffLine {
  type: "added" | "context" | "omitted" | "removed"
  text?: string
  count?: number
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}

function isStructuredJson(value: unknown): boolean {
  return Array.isArray(value) || (value !== null && typeof value === "object")
}

function stringifyValue(value: unknown): string {
  if (typeof value === "string") return value
  return JSON.stringify(value, null, 2) ?? String(value ?? "")
}

export function prepareApplyDiffValue(
  currentValue: unknown,
  proposedValue: string
): ApplyDiffValue {
  if (currentValue === undefined) {
    return {
      kind: "unavailable",
      previous: "",
      proposed: proposedValue,
    }
  }

  const parsedCurrent =
    typeof currentValue === "string" ? parseJson(currentValue) : currentValue
  const parsedProposed = parseJson(proposedValue)

  if (isStructuredJson(parsedCurrent) && isStructuredJson(parsedProposed)) {
    return {
      kind: "json",
      previous: stringifyValue(parsedCurrent),
      proposed: stringifyValue(parsedProposed),
    }
  }

  return {
    kind: "text",
    previous: stringifyValue(currentValue),
    proposed: proposedValue,
  }
}

function splitLinesWithEndings(value: string): string[] {
  return value.match(/[^\n]*\n|[^\n]+$/g) ?? []
}

function encodeLines(previous: string, proposed: string) {
  const lineArray = [""]
  const lineToCode = new Map<string, number>()

  const encode = (value: string) => {
    let encoded = ""
    for (const line of splitLinesWithEndings(value)) {
      let code = lineToCode.get(line)
      if (code === undefined) {
        if (lineArray.length >= MAX_LINE_TOKENS) return undefined
        code = lineArray.length
        lineToCode.set(line, code)
        lineArray.push(line)
      }
      encoded += String.fromCharCode(code)
    }
    return encoded
  }

  const encodedPrevious = encode(previous)
  const encodedProposed = encode(proposed)
  if (encodedPrevious === undefined || encodedProposed === undefined) {
    return undefined
  }
  return { encodedPrevious, encodedProposed, lineArray }
}

function stripLineEnding(value: string): string {
  return value.endsWith("\n") ? value.slice(0, -1) : value
}

function expandLineDiffs(
  diffs: Array<[DiffOperation, string]>,
  lineArray: string[]
): ApplyDiffLine[] {
  const lines: ApplyDiffLine[] = []
  for (const [operation, encodedLines] of diffs) {
    const type =
      operation === DIFF_INSERT
        ? "added"
        : operation === DIFF_DELETE
          ? "removed"
          : "context"
    for (const code of encodedLines) {
      lines.push({ type, text: stripLineEnding(lineArray[code.charCodeAt(0)]) })
    }
  }
  return lines
}

export function buildApplyLineDiff(
  previous: string,
  proposed: string
): ApplyDiffLine[] {
  const encoded = encodeLines(previous, proposed)
  if (!encoded) {
    return [
      ...splitLinesWithEndings(previous).map((text) => ({
        type: "removed" as const,
        text: stripLineEnding(text),
      })),
      ...splitLinesWithEndings(proposed).map((text) => ({
        type: "added" as const,
        text: stripLineEnding(text),
      })),
    ]
  }

  const dmp = new diff_match_patch()
  const diffs = dmp.diff_main(
    encoded.encodedPrevious,
    encoded.encodedProposed,
    false
  ) as Array<[DiffOperation, string]>
  return expandLineDiffs(diffs, encoded.lineArray)
}

export function compactApplyLineDiff(
  lines: ApplyDiffLine[],
  contextLines = 2
): ApplyDiffLine[] {
  const changedIndexes = lines.flatMap((line, index) =>
    line.type === "context" ? [] : [index]
  )
  if (changedIndexes.length === 0) return lines

  const visibleIndexes = new Set<number>()
  changedIndexes.forEach((index) => {
    for (
      let nearby = Math.max(0, index - contextLines);
      nearby <= Math.min(lines.length - 1, index + contextLines);
      nearby += 1
    ) {
      visibleIndexes.add(nearby)
    }
  })

  const compacted: ApplyDiffLine[] = []
  for (let index = 0; index < lines.length; index += 1) {
    if (visibleIndexes.has(index)) {
      compacted.push(lines[index])
      continue
    }

    const omittedStart = index
    while (index + 1 < lines.length && !visibleIndexes.has(index + 1)) {
      index += 1
    }
    compacted.push({
      type: "omitted",
      count: index - omittedStart + 1,
    })
  }
  return compacted
}

export function isShortApplyText(diff: ApplyDiffValue): boolean {
  return (
    diff.kind === "text" &&
    !diff.previous.includes("\n") &&
    !diff.proposed.includes("\n") &&
    Math.max(diff.previous.length, diff.proposed.length) <= 120
  )
}

export function hasApplyDiffChanges(diff: ApplyDiffValue): boolean {
  return diff.previous !== diff.proposed
}
